import { useFormik } from 'formik';
import React, { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSwipeable } from 'react-swipeable';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import { IContactMainInfo } from '../../../../common/models/contact/IContactMainInfo';
import { IReferenceSurvey } from '../../../../common/models/reference/IReferenceSurvey';
import { confirmContactInfoValidationSchema } from '../../../../common/validation/yup';
import Icon from '../../../../components/Icon';
import IconButton from '../../../../components/IconButton';
import Input from '../../../../components/Input';
import RegularButton from '../../../../components/RegularButton';

interface IProps {
  reference: IReferenceSurvey,
  candidateFirstName: string;
  onSubmit: IBindingCallback1<Omit<IContactMainInfo, 'id'>>;
  goBack: IBindingAction;
}

const ConfirmContactInfoForm: FunctionComponent<IProps> = ({
  candidateFirstName,
  reference,
  onSubmit,
  goBack
}) => {
  const { firstName, lastName, email, phoneNumber } = reference;

  const { handleSubmit, setFieldValue, values, isValid, errors } = useFormik({
    initialValues: { firstName, lastName, email, phoneNumber: phoneNumber || '' },
    onSubmit,
    validationSchema: confirmContactInfoValidationSchema,
    validateOnMount: true
  });

  const swipeHandler = useSwipeable({
    onSwipedLeft: () => handleSubmit(),
    onSwipedRight: goBack,
    trackMouse: false
  });

  return (
    <div {...swipeHandler} className="survey-page">
      <div className="survey-form-container">
        <span className="survey-page-title mb-4">
          <FormattedMessage id="survey.confirmInfo" defaultMessage="Please confirm your information" />
        </span>
        <div className="confirm-info-description">
          <span className="survey-page-text">
            <FormattedMessage
              id="survey.survey.confirmInfoDesc"
              defaultMessage={`{firstName} provided us the following information. Please edit / provide any missing 
              information and click “Next” when everything looks good! `}
              values={{
                firstName: candidateFirstName
              }}
            />
          </span>
        </div>
        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="survey-form">
            <div className="survey-inputs-row">
              <Input
                size="lg"
                onChange={val => setFieldValue('firstName', val)}
                label={{ id: 'firstName', defaultMessage: 'First name' }}
                value={values.firstName}
                variant="underlined"
                error={Boolean(errors.firstName)}
              />
              <Input
                size="lg"
                onChange={val => setFieldValue('lastName', val)}
                label={{ id: 'lastName', defaultMessage: 'Last name' }}
                value={values.lastName}
                variant="underlined"
                error={Boolean(errors.lastName)}
              />
            </div>
            <div className="survey-inputs-row">
              <Input
                size="lg"
                onChange={val => setFieldValue('email', val)}
                label={{ id: 'survey.email.placeholder', defaultMessage: 'Best email to reach you' }}
                value={values.email}
                variant="underlined"
                error={Boolean(errors.email)}
                type="email"
              />
              <Input
                size="lg"
                onChange={val => setFieldValue('phoneNumber', val)}
                label={{ id: 'survey.phoneNumber.placeholder', defaultMessage: 'Best phone number to reach you' }}
                value={values.phoneNumber}
                variant="underlined"
                error={Boolean(errors.phoneNumber)}
                type="phoneNumber"
                dropdownPosition="top"
              />
            </div>
          </div>
          <div className="d-flex justify-content-end">
            <RegularButton
              size="lg"
              textIntlId="survey.next"
              defaultMessage="Next"
              variant="primary"
              onClick={handleSubmit}
              disabled={!isValid}
              icon={<Icon icon="arrowRight" />}
              iconPosition="right"
              type="submit"
            />
          </div>
        </form>
        <div className="survey-back-bttn">
          <IconButton
            onClick={() => goBack()}
            icon={<Icon icon="arrowLeft" className="backButtonIcon" />}
            variant="bordered"
          />
        </div>
      </div>
    </div>
  );
};

export default ConfirmContactInfoForm;
